// =============================================================================
// ui/tabs/Trades.tsx — the trade list.
// -----------------------------------------------------------------------------
// Rebuilt on the range-scoped table read (D57). The first version rendered 7 of
// the old client's 16 columns, and the 8 that were missing were not even in the
// loaded projection — `entry`, `exit`, `side`, `timeOut`, `maePoints`,
// `mfePoints`, `grossPnl` and `fees` are absent from METRICS_KEYS by design.
// Widening the metrics read to serve a table would have taxed every aggregate
// forever; the second read is scoped to the range instead.
//
// ── WHAT CHANGED, AND WHY THE 45-DAY NOTICE IS GONE ──
//
// The first version read from the journal's bounded boot Population and said so
// in a caveat: "showing the last 45 days". That was honest, but it described the
// LOADER rather than anything the user set. This reads exactly the active date
// range and pages it to exhaustion, so the table covers precisely what the
// toolbar claims — no window to caveat, and the range picker is the control that
// changes it.
//
// An empty result therefore means something specific and true: no trades in THIS
// range. Under `All time` that is genuinely an empty book, and the copy differs.
//
// ── ⚠ A NOTE ABOUT THIS BANNER, WHICH WAS WRONG FOR A WHILE ──
//
// It used to list "Add Trade, the row drilldown, delete, bulk selection and the
// bulk action bar" as not-yet-here. All five have shipped since, and the banner
// kept saying otherwise — the same rot that hit the journal's "what's writable"
// card three times in one day (D68). A file header that enumerates what is
// missing goes stale silently, because nothing renders it and nothing tests it.
//
// So it states a RULE instead: **every editor on this tab hydrates before it
// renders a field.** The row click and Add Trade both open `TradeDetail`, which
// does its own full read and refuses to render a form if that read fails; bulk
// edit takes the other route and writes named COLUMNS rather than rows (D70).
// Import is a third case — its rows are constructed here, complete, so `markNew`
// is an honest ledger claim.
//
// Anything that opens an editor from this tab has to pick one of those three.
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTabMemory } from '../state/tabMemory';
import type { TradesIntent } from '../shell/TradeActions';
import { TradeDetail } from './TradeDetail';
import { ImportTrades } from './ImportTrades';
import { ImportHistory } from './ImportHistory';
import { useAccounts } from '../state/accounts';
import { useContracts } from '../state/contracts';
import { usePnlUnit } from '../state/usePnlUnit';
import { tradeValue } from '../../selectors/pnlUnit';
import { useActiveFacets, useFilters } from '../state/filters';
import { useRange } from '../state/range';
import { useTradeRows } from '../state/useTradeRows';
import type { TradeTableRow } from '../../io/sync/trades';
import { effectiveTradeDate } from '../../selectors/population';
import { selectKpis, type Kpis } from '../../selectors/kpis';
import { applyFacets, tradesForSelection, type Account } from '../../selectors/accounts';
import { useSupabase } from '../state/supabase';
import { applyBulkEdit, deleteTrades, loadBulkEditRows } from '../../io/sync/bulkEdit';
import { emptyBulkPlan, type BulkEditPlan, type BulkEditTrade } from '../../selectors/bulkEdit';
import { BulkActionBar } from './BulkActionBar';
import { BulkEditModal } from './BulkEditModal';
import { num, pct, pnlClass, unitValue } from '../format';
import {
  DEFAULT_SORT, nextSort, sortTrades, type TradeSort,
} from './tradeColumns';
// ⚠ The table is SHARED with the Dashboard now — see `TradeTable.tsx`. It lived
// here as a local component while the Dashboard hand-rolled a six-column one, and
// the two had already drifted.
import { TradeTable, makeAccountLabeller, type AccountLabeller } from './TradeTable';
import { reportFailure } from '../../io/diagnostics/errorLog';


export function TradesTab(
  { intent = null, onIntentHandled }:
  {
    /**
     * What the toolbar asked for on the way in — `'add'` opens the blank trade
     * form, `'import'` brings the import card into view. Null when the user
     * navigated here themselves.
     *
     * ⚠ Owned by `App`, ABOVE the shell's per-tab remount. See the note there:
     * holding it any lower means the `navigate('trades')` that sets it destroys
     * it in the same breath.
     */
    intent?: TradesIntent | null;
    /** Clears the intent so a later remount does not replay it. */
    onIntentHandled?: () => void;
  } = {},
) {
  const { bounds, mode, label } = useRange();
  const { accounts } = useAccounts();
  const { accounts: selection } = useFilters();
  // The global display unit — $ / points / ticks / R. See `UnitPicker`.
  // ⚠ `useActiveFacets`, never `storedFacets` — see `state/filters.tsx`.
  const { facets } = useActiveFacets();
  const { contracts } = useContracts();
  // This tab converts fully — selector AND formatters (#44).
  const { unit } = usePnlUnit();
  /**
   * One trade’s value in the display unit, for the table’s P&L column.
   *
   * ⚠ The table takes this FUNCTION rather than the contract catalogue — it has
   * no other reason to know what a tick size is. Null where the trade has no
   * value in the unit (no stop, no R), which the cell renders as an em-dash.
   */
  const valueOf = useCallback(
    // 🚨 NULL IN, NULL OUT. `tradeValue` coerces a null P&L to 0 because that is
    // right for SUMMING — a trade with no result contributes nothing. It is wrong
    // for a CELL: an open or unpriced trade would render $0.00 and read as a
    // scratch, which is a different and confident claim. Caught by the table’s
    // own test, which has asserted the em-dash since the port began.
    (t: TradeTableRow) => (t.netPnl == null ? null : tradeValue(t as never, unit, contracts ?? [])),
    [unit, contracts],
  );
  const { sb, userId } = useSupabase();

  // ⚠ Bumped after an import so the table re-reads. The import writes directly,
  // so nothing else would tell this tab its data changed — and a table that still
  // shows the old count after a successful import reads as a failed import.
  const [reloadNonce, setReloadNonce] = useState(0);
  const { phase, rows, error } = useTradeRows(bounds, mode, reloadNonce);
  const [sort, setSort] = useTabMemory<TradeSort>('trades:sort', DEFAULT_SORT);

  // ── 🚨 THE ID ONLY, NEVER THE ROW ──
  //
  // The editor re-reads the trade in full for itself. Handing it the row already
  // on screen is the D46 mistake — the row carries 23 of 48 columns, so an editor
  // populated from it shows BLANK for notes, grade, pattern and the discipline
  // booleans, and a save then writes those blanks back with full authorisation.
  //
  // ⚠ THREE states, not two: `null` is closed, `{ id: null }` is Add Trade, and
  // `{ id }` is edit. A single `string | null` cannot express "open on a trade
  // that does not exist yet" — which is exactly the case that must NOT hydrate.
  const [editing, setEditing] = useState<{ id: string | null } | null>(null);
  const openTrade = useCallback((id: string) => setEditing({ id }), []);

  /**
   * Act on what the toolbar asked for, once, on the way in.
   *
   * ⚠ `'add'` opens the SAME `{ id: null }` state the tab's own "+ Add Trade"
   * button sets — not a second path into the form. Two ways to open a blank
   * trade would drift on exactly the fields nobody checks, which is the reason
   * that button opens the shared editor rather than a quick-add of its own.
   *
   * ⚠ `'import'` SCROLLS rather than opening anything, because the import card
   * is already on this tab and always visible. Auto-opening a file picker would
   * be worse than useless: a browser only honours one from a real click, so it
   * would be silently blocked about as often as it worked.
   *
   * ⚠ The intent is cleared IMMEDIATELY — see the note where it is owned. Left
   * set, walking away and back would replay it.
   *
   * ── 🚨 THE SCROLL CANNOT RUN IN THIS EFFECT, AND THE FIRST VERSION DID ──
   *
   * This tab RETURNS EARLY with "Loading your trades…" until the read settles,
   * so when the toolbar navigates here the import card does not exist yet. The
   * first version called `importRef.current?.scrollIntoView()` straight from
   * this effect: `current` was null, the optional chain swallowed it, and the
   * button did nothing at all. Correct code that never runs — the exact class
   * this port has shipped four times (D149, D151, D152, D157).
   *
   * ▶ So the scroll is armed here and FIRED BY THE CALLBACK REF below, which
   * runs the moment the node actually mounts. `armScroll` covers the other
   * order too — already on Trades, node already there, no remount — because a
   * callback ref does not re-fire for a node that never left.
   *
   * ⚠ Caught by the test, not by reading it back. It asserts the scroll HAPPENED
   * rather than that the intent was received.
   */
  const importNode = useRef<HTMLDivElement | null>(null);
  const scrollPending = useRef(false);

  const scrollToImport = useCallback((node: HTMLDivElement) => {
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  /** Scrolls now if the card is mounted, otherwise the moment it mounts. */
  const armScroll = useCallback(() => {
    if (importNode.current) { scrollToImport(importNode.current); return; }
    scrollPending.current = true;
  }, [scrollToImport]);

  // ⚠ A CALLBACK ref, not an object ref. An object ref gives no notification
  // when the node appears, which is the whole problem above.
  const importRef = useCallback((node: HTMLDivElement | null) => {
    importNode.current = node;
    if (node && scrollPending.current) {
      scrollPending.current = false;
      scrollToImport(node);
    }
  }, [scrollToImport]);

  useEffect(() => {
    if (!intent) return;
    onIntentHandled?.();
    if (intent === 'add') setEditing({ id: null });
    else armScroll();
  }, [intent, onIntentHandled, armScroll]);

  const labelAccount = useMemo<AccountLabeller>(
    () => makeAccountLabeller(accounts ?? []),
    [accounts],
  );

  /**
   * The three lenses of the global filter, in the old client's order.
   *
   *   account → date → facets
   *
   * DATE is already applied — it is the bounds the read was issued with. The
   * other two are in-memory, and both narrow the LIST as well as the numbers.
   *
   * ⚠ `selectKpis` applies the account selection AGAIN internally, and that is
   * not redundant: it also needs the selection to decide whether the
   * exclude-from-metrics rule lifts (drilling into a SIM account alone should
   * show its numbers). Filtering here is "what is in view"; filtering there is
   * "what may move a number".
   */
  const inScope = useMemo(() => {
    if (!rows || !mode) return null;
    return tradesForSelection(applyFacets(rows, facets, mode), selection, accounts ?? []);
  }, [rows, facets, mode, selection, accounts]);

  const split = useMemo(() => {
    if (!inScope || !mode) return null;
    const sorted = sortTrades(inScope, sort, mode, labelAccount);
    return {
      // `=== false` and `!== false`, matching the old client exactly. A trade
      // whose flag is null or undefined is NOT "needs review" — that is the
      // shape of every trade written before the flag existed, and treating it
      // as unreviewed would move a whole history into the queue.
      unannotated: sorted.filter((t) => t.annotated === false),
      annotated: sorted.filter((t) => t.annotated !== false),
    };
  }, [inScope, sort, mode, labelAccount]);

  const kpis = useMemo<Kpis<TradeTableRow> | null>(() => {
    if (!inScope || !accounts || !contracts) return null;
    // ⚠ CLASS W. The claim is "these are the numbers for the range on screen",
    // and the read covers exactly that range — the same predicate applied to the
    // same set. Nothing population-scoped (balance, drawdown, consistency)
    // appears on this tab, and nothing here may start doing so under a read
    // bounded by a date filter.
    return selectKpis({
      trades: inScope,
      accounts: accounts as readonly Account[],
      contracts,
      selection,
      unit,
    });
  }, [inScope, accounts, contracts, selection]);

  const onSort = useCallback(
    (key: string) => setSort((cur) => nextSort(cur, key)),
    [],
  );

  // ── Bulk selection ─────────────────────────────────────────────────────────
  //
  // ⚠ IDS ONLY, again. Holding the row objects would put a 23-of-48-column
  // projection into the thing that feeds a write — `applyBulkEdit` re-reads the
  // editable fields for itself, and that read is what makes the preserve modes
  // and the overwrite warning honest.
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState<readonly BulkEditTrade[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const toggleRow = useCallback((id: string) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // ⚠ Scoped to the rows CURRENTLY VISIBLE in that table, not the whole book.
  // A select-all that reached past the filter would hand Delete a set the user
  // cannot see.
  const toggleAll = useCallback((ids: readonly string[], on: boolean) => {
    setSelected((cur) => {
      const next = new Set(cur);
      for (const id of ids) { if (on) next.add(id); else next.delete(id); }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelected(new Set()), []);
  const selectedIds = useMemo(() => [...selected], [selected]);

  /**
   * The modal's header summary.
   *
   * ⚠ Computed from the TABLE rows, not from the bulk read — the table already
   * has date, contract, account and P&L, and the bulk read deliberately does not
   * carry them. Asking the bulk read for them would widen it, and a wider read is
   * what tempts a caller to assemble a row from it.
   */
  const bulkScope = useMemo(() => {
    const rowsIn = (inScope ?? []).filter((t) => selected.has(String(t.id)));
    const dates = rowsIn
      .map((t) => (mode ? String(effectiveTradeDate(t, mode) ?? '') : ''))
      .filter((d) => !!d).sort();
    const contractsIn = [...new Set(rowsIn.map((t) => String(t.contract ?? '')).filter(Boolean))].sort();
    const accountsIn = new Set(rowsIn.map((t) => String(t.accountId ?? '')));
    return {
      dateMin: dates[0] ?? '', dateMax: dates[dates.length - 1] ?? '',
      contracts: contractsIn, accounts: accountsIn.size,
      wins: rowsIn.filter((t) => (t.netPnl ?? 0) > 0).length,
      losses: rowsIn.filter((t) => (t.netPnl ?? 0) < 0).length,
      totalPnl: rowsIn.reduce((s, t) => s + (t.netPnl ?? 0), 0),
      // ⚠ From the TABLE rows, which carry `annotated` — the same field the
      // Needs Review split reads. The bulk read deliberately does not carry it.
      unannotated: rowsIn.filter((t) => t.annotated === false).length,
    };
  }, [inScope, selected, mode]);

  /**
   * The ‹ › order in the trade form.
   *
   * ⚠ CHRONOLOGICAL, not the table's current sort. The old client sorts this list
   * by `date + timeIn` regardless of how the table is ordered, and that is the
   * right call: stepping "next" while the table is sorted by P&L would walk the
   * user through their trades in order of size, which is not a sequence anyone
   * reviews in. It is scoped to what is IN VIEW, so the arrows never leave the
   * filter.
   */
  const siblingIds = useMemo(() => {
    if (!inScope || !mode) return [] as string[];
    return [...inScope]
      .sort((a, b) =>
        `${effectiveTradeDate(a, mode)} ${a.timeIn ?? ''}`
          .localeCompare(`${effectiveTradeDate(b, mode)} ${b.timeIn ?? ''}`))
      .map((t) => String(t.id));
  }, [inScope, mode]);

  const afterWrite = useCallback((msg: string | null) => {
    setBulkError(msg);
    if (msg) return;
    setSelected(new Set());
    setBulkOpen(false);
    setBulkRows(null);
    setReloadNonce((n) => n + 1);
  }, []);

  const openBulkEdit = useCallback(async () => {
    setBusy(true); setBulkError(null);
    try {
      setBulkRows(await loadBulkEditRows(sb, selectedIds));
      setBulkOpen(true);
    } catch (e) {
      setBulkError(reportFailure('bulk edit', e));
    } finally { setBusy(false); }
  }, [sb, selectedIds]);

  const runApply = useCallback(async (plan: BulkEditPlan) => {
    if (!bulkRows) return;
    setBusy(true); setBulkError(null);
    const res = await applyBulkEdit({ sb, userId, rows: bulkRows, plan });
    setBusy(false);
    afterWrite(res.error);
  }, [sb, userId, bulkRows, afterWrite]);

  const runMarkAnnotated = useCallback(async () => {
    setBusy(true); setBulkError(null);
    // ⚠ Built from `emptyBulkPlan()` rather than a hand-rolled `{annotated:true}`
    // patch, so this one-click action goes through the same engine — and the same
    // "off sections contribute no key" guarantee — as the modal.
    const plan = emptyBulkPlan();
    const rows = selectedIds.map((id) => ({ id }) as BulkEditTrade);
    const res = await applyBulkEdit({ sb, userId, rows, plan });
    setBusy(false);
    afterWrite(res.error);
  }, [sb, userId, selectedIds, afterWrite]);

  const runDelete = useCallback(async () => {
    const n = selectedIds.length;
    // ⚠ Hard delete. There is no soft-delete on `trades`, so the confirmation
    // has to say the count and say it is permanent.
    if (!window.confirm(
      `Delete ${n} trade${n === 1 ? '' : 's'} permanently?\n\n`
      + 'This cannot be undone — trades have no 30-day recovery window.',
    )) return;
    setBusy(true); setBulkError(null);
    const res = await deleteTrades(sb, userId, selectedIds);
    setBusy(false);
    afterWrite(res.error);
  }, [sb, userId, selectedIds, afterWrite]);

  // ── states ─────────────────────────────────────────────────────────────────

  if (phase === 'failed') {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your trades</h3>
        <p className="muted">
          Nothing has been changed — this is a read problem. {error}
        </p>
      </div>
    );
  }

  if (!bounds || !mode || !split || !accounts || !contracts) {
    return <p className="muted">Loading your trades…</p>;
  }

  const total = split.unannotated.length + split.annotated.length;

  return (
    <div className="trades-tab">
      <div className="section-header">
        <h3>Trades ({total})</h3>
        {/* ⚠ The range label used to sit here as `<span className="muted">{label}</span>`
            and the parity screenshot removed it. Beta states the scope ONCE, in the
            page chrome ("Today · All accounts"), and its section header carries only
            the count and the actions. This client's FilterBar already prints
            "All time · All accounts" two inches above, so the second copy was both a
            divergence and the thing D65 warns about — two renderings of one fact on
            one screen, free to disagree. */}
        <span className="grow" />
        {/* ── ⚠ A VISIBLE DISABLED PLACEHOLDER, NOT AN ABSENCE ──
            Beta has a working `Coach Me — This Week` here
            (app/index.html:30715, `id="t-coach"`). Rendering nothing made
            Trades the ONE tab silent about an unported control, while
            Dashboard.tsx and DayOverview.tsx both already ship exactly this —
            so a bare header read as "beta never had this" rather than "this is
            coming". A parity check has to be able to tell dropped from
            pending. (TCR-26, finding 1.)

            ⚠ NO `data-noshare`, unlike the Dashboard's copy, and that is a
            CHECKED difference rather than an inconsistency to tidy away: beta
            marks its DASHBOARD button (:28091) and does NOT mark this one
            (:30715). The rule is beta's markup, not this client's other
            screens. Verified 2026-09-17 and pinned by a test, because nothing
            registers a share source yet (ShareButton.tsx returns null) so no
            rendered report can catch it going wrong. */}
        <button type="button" className="btn" disabled title="Not ported yet">
          Coach Me — This Week
        </button>
        {/* ⚠ Opens the SAME form as a row click, with no id. A separate "quick
            add" form would be a second place trades come from, and the two would
            drift on exactly the fields nobody checks. */}
        <button type="button" className="btn primary" onClick={() => setEditing({ id: null })}>
          + Add Trade
        </button>
      </div>

      {/* ⚠ Wrapped so the toolbar's Import can bring it into view. The ref is on
          a node this tab owns rather than reaching into `ImportTrades`, which
          keeps the scroll target from depending on that component's markup. */}
      <div ref={importRef}>
        <ImportTrades onImported={() => setReloadNonce((n) => n + 1)} />
      </div>
      {/* ⚠ Shares `reloadNonce` in BOTH directions: an import refreshes the
          history, and an undo refreshes the table. Either one stale is a screen
          disagreeing with the book it is showing. */}
      <ImportHistory
        nonce={reloadNonce}
        onUndone={() => setReloadNonce((n) => n + 1)}
      />

      {bulkOpen && bulkRows ? (
        <BulkEditModal
          rows={bulkRows}
          scope={bulkScope}
          busy={busy}
          error={bulkError}
          onCancel={() => { setBulkOpen(false); setBulkRows(null); setBulkError(null); }}
          onApply={(plan) => void runApply(plan)}
          onDelete={() => void runDelete()}
        />
      ) : null}

      {editing ? (
        <TradeDetail
          // ⚠ NO `key` HERE, deliberately. `TradeDetail` owns the reset — it nulls
          // its loaded row before re-reading, which unmounts the form and reseeds
          // it. A third copy of the same guard in a third file is not extra safety;
          // it is two more places to edit when the rule changes, and the two that
          // are not edited go on looking authoritative.
          //
          // ⚠ THIS WAS MEASURED, not assumed. Three guards existed — this key, a
          // key inside TradeDetail, and the null — and removing any ONE of them
          // left `opening a SECOND row` green, because the other two covered.
          // Removing all three failed it with the real symptom: `expected 'trade
          // one' to be 'trade two'`. A guard whose control cannot fire is not a
          // guarantee, so the redundant one is gone.
          tradeId={editing.id}
          siblingIds={siblingIds}
          onNavigate={openTrade}
          onSaved={() => { setEditing(null); setReloadNonce((n) => n + 1); }}
          onDeleted={() => { setEditing(null); setReloadNonce((n) => n + 1); }}
          onCancel={() => setEditing(null)}
        />
      ) : null}

      {phase === 'loading' ? (
        // Shown ALONGSIDE the previous rows rather than instead of them: changing
        // the range should not blank the screen it is filtering.
        <p className="muted" role="status">Updating…</p>
      ) : null}

      {total === 0 ? (
        <EmptyState unbounded={!bounds.startDate && !bounds.endDate} label={label} />
      ) : (
        <>
          <BulkActionBar
            count={selected.size} busy={busy}
            onClear={clearSelection}
            onBulkEdit={() => void openBulkEdit()}
            onMarkAnnotated={() => void runMarkAnnotated()}
            onDelete={() => void runDelete()}
          />
          {/* ⚠ The bar's own errors surface HERE, not inside the modal, because
              the read that opens the modal can fail before there is a modal. */}
          {bulkError && !bulkOpen ? <p className="red" role="alert">{bulkError}</p> : null}

          {kpis ? <KpiStrip k={kpis} /> : null}

          {split.unannotated.length ? (
            <>
              <div className="section-header">
                <h3 className="blue">Needs Review ({split.unannotated.length})</h3>
                <span className="muted">
                  Already counted in your P&amp;L &amp; balances — tag these to unlock
                  pattern, confluence &amp; exit-model analytics
                </span>
              </div>
              <TradeTable
                unit={unit}
                valueOf={valueOf}
                rows={split.unannotated} kind="unannotated" mode={mode} labelAccount={labelAccount}
                onOpen={openTrade}
                selection={{
                  sort, onSort, selected, onToggleRow: toggleRow, onToggleAll: toggleAll,
                }}
              />
            </>
          ) : null}

          {split.annotated.length ? (
            <>
              <div className="section-header">
                <h3>
                  {split.unannotated.length ? 'Reviewed' : 'All Trades'} ({split.annotated.length})
                </h3>
              </div>
              <TradeTable
                unit={unit}
                valueOf={valueOf}
                rows={split.annotated} kind="annotated" mode={mode} labelAccount={labelAccount}
                onOpen={openTrade}
                selection={{
                  sort, onSort, selected, onToggleRow: toggleRow, onToggleAll: toggleAll,
                }}
              />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * ⚠ Two different facts, and conflating them is a lie a user ACTS on.
 *
 * "No trades on file yet" after filtering to one week sends someone to
 * re-import data they already have. The distinction is now cheap and exact,
 * because the read covers precisely the range: an empty UNBOUNDED range really
 * is an empty book.
 */
function EmptyState({ unbounded, label }: { unbounded: boolean; label: string }) {
  return (
    <div className="card">
      <h3>{unbounded ? 'No trades on file yet' : `No trades in ${label.toLowerCase()}`}</h3>
      <p className="muted">
        {unbounded
          ? 'Import a broker export to get started.'
          : 'Widen the date range in the toolbar to look further back.'}
      </p>
    </div>
  );
}

/**
 * The portfolio strip — over every in-range trade, reviewed or not.
 *
 * ⚠ Gated on trade COUNT, never on annotation. Imported trades carry valid net
 * P&L the moment they land; the old client once gated this on the annotated set
 * and a freshly-imported account showed no stats at all.
 */
function KpiStrip({ k }: { k: Kpis<TradeTableRow> }) {
  // 🚨 `bestValue`, NOT `bestTrade.netPnl`. The ranking is done on the display
  // unit’s value while the trade object’s own `netPnl` is always dollars — so
  // reading it names the right trade and states the wrong result. Same defect
  // the Dashboard had.
  const best = k.bestValue;
  const worst = k.worstValue;
  return (
    <div className="trade-stats-strip" data-testid="kpi-strip">
      <Stat label="Trades" value={String(k.count)} sub="in scope" />
      <Stat label="Win Rate" value={pct(k.winRate, 0)} sub={`${k.wins}W / ${k.losses}L`} />
      {/* ⚠ "net of fees" only means something in dollars — fees do not move
          points, so the caption goes when the unit does. */}
      <Stat
        label="Net P&L" value={unitValue(k.totalPnl, k.unit)} cls={pnlClass(k.totalPnl)}
        sub={k.unit === 'usd' ? 'net of fees' : ''}
      />
      <Stat label="Avg Win" value={unitValue(k.avgWin, k.unit)} cls="green" />
      <Stat label="Avg Loss" value={unitValue(k.avgLoss, k.unit)} cls="red" />
      <Stat label="Avg R:R" value={num(k.avgRR, 2)} />
      <Stat
        label="Best / Worst" value={unitValue(best, k.unit)} cls="green"
        sub={worst != null ? `/ ${unitValue(worst, k.unit)}` : ''} subCls="red"
      />
    </div>
  );
}

function Stat(
  { label, value, sub, cls, subCls }:
  { label: string; value: string; sub?: string; cls?: string; subCls?: string },
) {
  return (
    <div className="stat-cell">
      <div className="slabel">{label}</div>
      <div className={`sval ${cls ?? ''}`}>{value}</div>
      {sub ? <div className={`ssub ${subCls ?? ''}`}>{sub}</div> : null}
    </div>
  );
}

